'use client';

import { css, cx } from '@onbeam/business-styled-system/css';
import {
  center,
  flex,
  linkOverlay,
  text,
  vstack,
} from '@onbeam/business-styled-system/patterns';
import { ArrowRightIcon } from '@onbeam/icons/business';
import Image from 'next/image';
import { BountyItemProps } from './BountyItem.types';
import { Drawer } from '@/components/shared/Drawer';
import { BountyDrawerContent } from './BountyDrawerContent';
import { applicationStyleMap } from '@/components/features/Applications/ApplicationsList/ApplicationsList.data';
import { useRef } from 'react';
import { useFocusVisibleWithin } from '@/helpers/useFocusVisibleWithin';

export const BountyItem = (bounty: BountyItemProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const focusVisible = useFocusVisibleWithin(containerRef);

  const { name, category, description, reward, applicationsCount } = bounty;
  const style = applicationStyleMap[category];
  const Icon = style.icon;

  return (
    <div
      ref={containerRef}
      data-focus-visible-within={focusVisible}
      className={cx(
        'group',
        vstack({
          position: 'relative',
          p: '4',
          gap: '3',
          rounded: 'md',
          border: '1px solid',
          borderColor: 'opacity.md',
          bg: 'mono.primaryFg',
          color: 'mono.primaryBg',
          shadow: 'md',
          w: 'full',
          h: 'full',
          transition: 'shadow',

          md: {
            maxW: '[21.75rem]',
            flexShrink: '0',
            p: '5',
          },
          _supportsHover: {
            _hover: {
              shadow: 'lg',
            },
          },
          '&[data-focus-visible-within="true"]': {
            shadow: 'lg',
          },
        }),
      )}
    >
      <div
        className={center({
          rounded: 'xs',
          position: 'relative',
          overflow: 'hidden',
          h: '[8.75rem]',
          w: 'full',
        })}
      >
        <Image
          priority
          className={css({
            position: 'absolute',
            inset: '0',
            objectFit: 'cover',
            w: 'full',
            h: 'full',
          })}
          width={430}
          height={168}
          src={style.bgImageUrl}
          alt=""
        />
        <div
          className={center({
            p: '3',
            rounded: 'md',
            border: '1px solid',
            borderColor: 'opacity.sm',
            bg: 'mono.primaryFg',
            zIndex: '0',
          })}
        >
          <Icon height={20} width={20} />
        </div>
      </div>
      <div className={vstack({ gap: '6' })}>
        <div className={vstack({ gap: '0.5' })}>
          <h3 className={text({ style: 'body-lg' })}>{name}</h3>
          <span className={text({ style: 'body-md' })}>
            <span className={text({ color: 'mono.tertiary' })}>Reward:</span>{' '}
            <span>{reward}</span>
          </span>
        </div>
        <p className={text({ style: { base: 'body-md', md: 'body-sm' } })}>
          {description}
        </p>
        <div className={flex({ justify: 'space-between' })}>
          <span
            className={text({
              style: 'small-sm',
              color: 'mono.tertiary',
              mt: 'auto',
            })}
          >
            {applicationsCount} Applications
          </span>
          <Drawer
            title={name}
            description={description}
            hideTitle
            hideDescription
            direction="right"
            trigger={
              <button
                type="button"
                data-focus-visible={focusVisible}
                className={cx(
                  linkOverlay(),
                  center({
                    cursor: 'pointer',
                    p: '2',
                    bg: 'mono.quartenery',
                    rounded: 'full',
                    transition: 'opacity',
                    opacity: 0,
                    _supportsHover: {
                      _groupHover: {
                        opacity: 1,
                      },
                      '&[data-focus-visible="true"]': {
                        opacity: 1,
                      },
                    },
                  }),
                )}
              >
                <ArrowRightIcon height={14} width={14} />
              </button>
            }
          >
            <BountyDrawerContent {...bounty} />
          </Drawer>
        </div>
      </div>
    </div>
  );
};
